import RecentDocumentService from 'business/services/RecentDocumentService';
import {} from 'components/Experiment/utils';
import Header from 'components/header/Header';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IStyle } from 'Styles';
import { SeedFormulationModal } from './Seed/SeedFormulationModal';
import { KeyboardKeys } from 'constants/';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';
import { navigationBlockWarningMessage } from 'utils/Global';

const saveDocumentToRecentlyOpenedList = async (seedId) => {
  const recentDocumentService = new RecentDocumentService();
  await recentDocumentService.saveDocumentToRecents(seedId);
};

const SeedDashboard = (props) => {
  const [seedName, setSeedName] = useState<string>('');
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const seedId = props.match.params.id;
  const seedRef = useRef(null);
  document.title = seedName;
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const [accessRightName, setAccessRightName] = useState<string>('');
  const isReadOnly = accessRightName !== ETreeNodeAccessRightNames.Modify;

  useEffect(() => {
    const openedWindows = JSON.parse(localStorage.getItem('openedNodesIds'));
    const openedWindowsList = !openedWindows ? [] : openedWindows;
    if (!openedWindows || !openedWindowsList.find((win) => win == seedId)) {
      openedWindowsList.push(seedId);
      localStorage.setItem('openedNodesIds', JSON.stringify(openedWindowsList));
    }

    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    });
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, [seedId]);

  const saveDiscardParams: UseSaveDiscardButtonsParams = useMemo(
    () => ({
      onSave: () => seedRef?.current?.refSaveSeed(),
      onDiscard: () => seedRef?.current?.discardChanges(),
      enableButtons: isDirty && seedRef?.current && !isReadOnly,
      navigationBlockCallback: () => {
        if (isDirty) {
          return navigationBlockWarningMessage;
        }
      },
    }),
    [isDirty, seedRef?.current],
  );
  const saveDiscardButtons = useSaveDiscardButtons(saveDiscardParams);

  useEffect(() => {
    saveDocumentToRecentlyOpenedList(seedId);
  }, [seedId]);

  const beforeUnloadListener = useCallback(
    (event) => {
      const openedWindows = JSON.parse(localStorage.getItem('openedNodesIds'));
      if (openedWindows && openedWindows.length) {
        const openedWindowsList = openedWindows.filter((win) => win != seedId);
        localStorage.setItem('openedNodesIds', JSON.stringify(openedWindowsList));
      }
    },
    [seedId],
  );

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (isDirty && seedRef?.current) {
          seedRef?.current?.refSaveSeed();
        }
      }
    },
    [isDirty && seedRef?.current, seedRef?.current?.refSaveSeed],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  const updateSeedName = (newSeedName: string) => {
    setSeedName(newSeedName);
    explorerBroadcastChannel?.postMessage({
      action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
      payload: { name: newSeedName, id: parseInt(seedId) },
    });
  };

  return (
    <div id='seeddashboard-wrapper' className="w-100 h-100">
      <Header title={seedName} isNewTheme={true} headerButtons={saveDiscardButtons} />
      <div id='seeddashboard-container' style={styles.seedContainer}>
        <SeedFormulationModal
          ref={seedRef}
          treeNodeId={seedId}
          setSeedName={updateSeedName}
          setIsSeedEdited={setIsDirty}
          isNew={false}
          setAccessRightName={setAccessRightName}
          accessRightName={accessRightName}
        />
      </div>
    </div>
  );
};

const styles: IStyle = {
  seedContainer: {
    paddingLeft: '5%',
    paddingRight: '5%',
  },
};
export default SeedDashboard;
